import { typography, radius } from '@/shared/theme/tokens';
import { valueBounds } from '../../liveModeMeta';
import { Sparkline } from '../../ui/Sparkline';
import {
  HUD, statusOf, arcPath, dialPoint, clamp01, GAUGE_START, GAUGE_SWEEP,
} from './hudTheme';

const MONO = typography.fontFamily.mono;
const CX = 60;
const CY = 60;
const R = 46;
const SW = 7;

/**
 * Arc gauge readout for one parameter: value-vs-band on a 270° dial with the
 * in-spec band lit faintly, a status-colored fill to the live value, a value
 * tick, the live number at center, and a trend sparkline. Stale rings grey out
 * and never animate. Read-only.
 */
export function GaugeRing({ alert, flash, animate }) {
  const stale = !!alert.isStale;
  const s = statusOf(alert.status, stale);

  const { lo, hi } = valueBounds(alert);
  const span = (hi - lo) || 1;
  const f = clamp01((alert.liveValue - lo) / span);

  const hasBand = alert.optimalMin != null && alert.optimalMax != null;
  const bf0 = hasBand ? clamp01((alert.optimalMin - lo) / span) : 0;
  const bf1 = hasBand ? clamp01((alert.optimalMax - lo) / span) : 0;

  const valueEnd = GAUGE_START + f * GAUGE_SWEEP;
  const dot = dialPoint(CX, CY, R, f);

  return (
    <div style={{
      position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
      padding: '12px 8px 10px', minWidth: 0, flex: 1,
      background: flash && !stale ? s.tint : 'transparent',
      borderRadius: radius.md,
      transition: animate ? 'background 0.5s ease' : 'none',
      animation: flash && !stale && animate ? 'hud-flash 0.9s ease-out' : 'none',
      opacity: stale ? 0.55 : 1,
    }}>
      {/* dial */}
      <div style={{ position: 'relative', width: 120, height: 96 }}>
        <svg viewBox="0 0 120 108" width="120" height="96" style={{ display: 'block', overflow: 'visible' }}>
          {/* track */}
          <path d={arcPath(CX, CY, R, GAUGE_START, GAUGE_START + GAUGE_SWEEP)}
            fill="none" stroke={HUD.panelDeep} strokeWidth={SW} strokeLinecap="round" />
          {/* in-spec band */}
          {hasBand && bf1 > bf0 && (
            <path
              d={arcPath(CX, CY, R, GAUGE_START + bf0 * GAUGE_SWEEP, GAUGE_START + bf1 * GAUGE_SWEEP)}
              fill="none" stroke={HUD_okTint(stale)} strokeWidth={SW} strokeLinecap="butt"
            />
          )}
          {/* value fill */}
          {!stale && f > 0.001 && (
            <path d={arcPath(CX, CY, R, GAUGE_START, valueEnd)}
              fill="none" stroke={s.color} strokeWidth={SW} strokeLinecap="round"
              style={{ filter: `drop-shadow(0 0 5px ${s.glow})` }} />
          )}
          {/* value tick */}
          {!stale && (
            <circle cx={dot.x} cy={dot.y} r={4} fill={HUD.void} stroke={s.color} strokeWidth={2}
              style={{ filter: `drop-shadow(0 0 4px ${s.glow})` }} />
          )}
        </svg>

        {/* center readout */}
        <div style={{
          position: 'absolute', inset: 0, top: -4, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', pointerEvents: 'none',
        }}>
          <div style={{
            fontFamily: MONO, fontSize: typography.fontSize['4xl'], fontWeight: typography.fontWeight.bold,
            color: stale ? HUD.inkFaint : s.color, fontVariantNumeric: 'tabular-nums', lineHeight: 1,
          }}>
            {stale ? '——' : alert.liveValue}
          </div>
          {alert.unit && (
            <div style={{ fontFamily: MONO, fontSize: typography.fontSize.xs, color: HUD.inkFaint, marginTop: 2 }}>
              {alert.unit}
            </div>
          )}
        </div>
      </div>

      {/* name */}
      <div style={{
        fontFamily: MONO, fontSize: typography.fontSize.xs, color: HUD.inkDim,
        letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase',
        maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center',
      }}>
        {alert.parameterName}
      </div>

      {/* trend */}
      <div style={{ width: '78%', height: 16, opacity: stale ? 0.4 : 0.9 }}>
        <Sparkline data={alert.buffer} color={stale ? HUD.inkFaint : s.color} height={16} animate={false} />
      </div>
    </div>
  );
}

// In-spec band stroke uses the OK tint live, near-invisible when stale.
function HUD_okTint(stale) {
  return stale ? HUD.strokeSoft : statusOf('ok', false).tint;
}
